import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth, isPlatformOwner, isSuperAdmin, getRoles } from "@/context/AuthContext";
import { useT, useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/common/BrandLogo";
import { toast } from "sonner";
import { LifeBuoy, LogOut } from "lucide-react";

/** Calm "access required" screen shown when a signed-in user has no valid
 *  invite, membership, or beta approval. Backend is the source of truth via
 *  GET /api/access/status. */
export default function AccessRequired() {
  const t = useT();
  const { lang } = useI18n();
  const { logout } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/access/status");
        if (!alive) return;
        setStatus(r.data);
      } catch { /* silent — screen is still meaningful */ }
    })();
    return () => { alive = false; };
  }, []);

  const requestAccess = () => nav("/request-access");

  const doLogout = async () => {
    setBusy(true);
    try { await logout(); } finally { setBusy(false); }
    nav("/login", { replace: true });
  };

  const title = lang === "nl"
    ? "Inner Compass is momenteel alleen op uitnodiging."
    : "Inner Compass is currently invite-only.";
  const body1 = lang === "nl"
    ? "Je hebt nog geen toegang."
    : "You do not have access yet.";
  const body2 = lang === "nl"
    ? "Vraag je organisatie, therapeut of zorgverlener om een uitnodiging."
    : "Please ask your organization, therapist or care provider for an invitation.";

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 p-6" data-testid="access-required">
      <div className="max-w-md w-full text-center animate-fade-up">
        <BrandLogo size="3xl" className="mx-auto mb-6" />
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-ink-900">
          {title}
        </h1>
        <p className="text-ink-700 mt-3">{body1}</p>
        <p className="text-ink-500 mt-1">{body2}</p>

        {status?.status && (
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white border border-border text-xs px-3 py-1 text-ink-500" data-testid="access-status">
            {lang === "nl" ? "Status" : "Status"}: <span className="text-ink-900 font-medium">{status.status}</span>
          </div>
        )}
        {status?.message && (
          <p className="text-sm text-ink-500 mt-3">{status.message}</p>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            onClick={requestAccess}
            data-testid="access-request"
            className="rounded-full bg-sage-500 hover:bg-sage-600 text-white h-11 px-5"
          >
            <LifeBuoy className="h-4 w-4 mr-1.5" />
            {lang === "nl" ? "Toegang aanvragen" : "Request access"}
          </Button>
          <Button
            variant="outline"
            onClick={doLogout}
            disabled={busy}
            data-testid="access-signout"
            className="rounded-full h-11 px-5 border-border"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            {lang === "nl" ? "Uitloggen" : "Sign out"}
          </Button>
        </div>
      </div>
    </div>
  );
}
